import { useCallback, useRef } from "react";
import { logger } from "../../../utils/logger";
import { hasNewDeployedVersion } from "../utils/appVersion";
import { useAppVisibility } from "./useAppVisibility";

/** Cambiar de app y volver enseguida no cuenta como "estuvo en segundo plano". */
const MIN_BACKGROUND_MS = 30_000;

/** Diálogos, drawers y menús de MUI cuelgan de un Modal: si hay uno abierto, alguien está a mitad de algo. */
const hasOpenOverlay = () => document.querySelector(".MuiModal-root") !== null;

/**
 * Al volver del segundo plano, si hubo un deploy mientras tanto, recarga la
 * app antes de que el usuario toque algo. Los chunks que todavía no se
 * pidieron ya no existen y el primer toque terminaría en pantalla en blanco.
 *
 * Con una ventana abierta no se recarga (se perdería lo que se estaba
 * cargando); en ese caso cubre AppErrorBoundary si un chunk falla después.
 */
export const useReloadOnNewVersion = () => {
  const checking = useRef(false);

  const onForeground = useCallback(async ({ timeInBackgroundMs }: { timeInBackgroundMs: number }) => {
    if (timeInBackgroundMs < MIN_BACKGROUND_MS || checking.current) return;
    checking.current = true;
    try {
      if (!(await hasNewDeployedVersion())) return;
      if (document.visibilityState !== "visible") return;
      if (hasOpenOverlay()) {
        logger.info("Hay una versión nueva; se pospone la recarga porque hay una ventana abierta");
        return;
      }
      logger.info("Hay una versión nueva desplegada; se recarga la app");
      window.location.reload();
    } finally {
      checking.current = false;
    }
  }, []);

  useAppVisibility({ onForeground });
};
